import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useSetPageProgress } from '../contexts/ProspectPageProgressContext';
import { useTenantApiToken } from '../contexts/TenantProfileContext';
import { selectionProgress } from '../utils/prospect-sidebar-progress';
import {
  getProspectSelection,
  type ProspectSelection,
  type ProspectSelectionStatus,
} from '../services/tenantApi';

const CARD = 'rounded-2xl border border-gray-200 bg-white p-4 shadow-bento dark:border-white/10 dark:bg-gray-800 sm:p-5';

const STATUS_LABEL: Record<ProspectSelectionStatus, string> = {
  pending: 'Pending',
  selected: 'Selected',
  not_selected: 'Not selected',
};

const STATUS_CLASS: Record<ProspectSelectionStatus, string> = {
  pending: 'bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300',
  selected: 'bg-green-50 text-green-800 dark:bg-green-950/30 dark:text-green-300',
  not_selected: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

const PENDING_NOTE = "We're currently evaluating applications. We will call you to inform you of the outcome of your application.";

function formatSubmittedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Submitted time not set';
  return date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function StatusBadge({ status }: { status: ProspectSelectionStatus }) {
  const badge = (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_CLASS[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
  if (status !== 'pending') return badge;
  return (
    <span className="group relative shrink-0">
      <span tabIndex={0} className="inline-flex cursor-default rounded-full outline-none focus-visible:ring-2 focus-visible:ring-amber-400">
        {badge}
      </span>
      <span
        role="tooltip"
        className="absolute right-0 top-full z-30 mt-2 hidden w-64 rounded-xl border border-gray-200 bg-white p-3 text-left text-sm font-normal leading-relaxed text-gray-700 shadow-bento group-hover:block group-focus-within:block dark:border-white/10 dark:bg-gray-900 dark:text-gray-200"
      >
        {PENDING_NOTE}
      </span>
    </span>
  );
}

export default function SelectionPage() {
  const getToken = useTenantApiToken();
  const setPageProgress = useSetPageProgress();
  const [selection, setSelection] = useState<ProspectSelection | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selection) return;
    setPageProgress('/selection', selectionProgress(selection.selectedUnitId));
  }, [selection, setPageProgress]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getProspectSelection(getToken)
      .then((data) => {
        if (!cancelled) setSelection(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unable to load selection.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  return (
    <div className="flex w-full flex-col gap-4 py-2 sm:py-4">
      <header className={CARD}>
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Step 7</p>
        <h1 className="mt-1 text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">Selection</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-gray-400 sm:text-base">
          Each home you have submitted, and where that application stands.
        </p>
      </header>

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading selection
        </div>
      )}
      {error && <p className="text-sm text-red-700 dark:text-red-400">{error}</p>}
      {!loading && !error && selection && selection.homes.length === 0 && (
        <section className={CARD}>
          <p className="text-sm text-gray-700 dark:text-gray-300">No home has been submitted yet.</p>
        </section>
      )}
      {!loading && selection && selection.homes.length > 0 && (
        <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {selection.homes.map((home) => (
            <li key={home.unitId} className={CARD}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-gray-900 dark:text-white">{home.label}</h2>
                  <p className="mt-3 text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">Submitted</p>
                  <p className="mt-0.5 text-sm text-gray-700 dark:text-gray-200">{formatSubmittedAt(home.submittedAt)}</p>
                </div>
                <StatusBadge status={home.status} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
